import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Button, Modal, Form, Alert } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faInfoCircle, faIndustry, faSave } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axiosInstance from '../../api/api';
import Ranked from '../components/Ciem/Ranked';
import CardLayout from '../components/Layout/CardLayout';

const EstructuraProduccionPage = () => {
    const [estructuraProduccion, setEstructuraProduccion] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showAyudaModal, setShowAyudaModal] = useState(false);
    const [showRecuerdeModal, setShowRecuerdeModal] = useState(false);

    const ciemId = useSelector(state => state.ciem.ciemId);
    const [rankedForm, setRankedForm] = useState([]);

    // Load estructura produccion from API
    useEffect(() => {
        if (ciemId) {
            fetchCiemData();
        } else {
            setLoading(false);
        }

        axiosInstance.get('/detailoption/option/value/rankedFourteen').then((response) => {
            console.log("Calificación obtenida:", response.data.data[0]);
            setRankedForm(response.data.data[0]);
        }).catch((e) => {
            alert("Error consultando la calificacion " + e)
        });
    }, [ciemId]);

    const fetchCiemData = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/ciem/${ciemId}`);
            // Handle nested data structure if needed
            const data = response.data.data || response.data;
            setEstructuraProduccion(data.estructuraProduccion || '');
            setError(null);
        } catch (err) {
            setError('Error al cargar los datos del CIEM: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!ciemId) {
            setError('No se ha seleccionado un CIEM');
            return;
        }

        try {
            setLoading(true);
            await axiosInstance.patch(`/ciem/estructuraProduccion/${ciemId}`, estructuraProduccion, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            setSuccess('Estructura de producción actualizada correctamente');
            setError(null);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Error al actualizar la estructura de producción: ' + err.message);
            setSuccess(null);
        } finally {
            setLoading(false);
        }
    };

    if (!ciemId) {
        return (
            <Container className="mt-4">
                <Alert variant="warning">
                    Por favor, seleccione un CIEM para editar la estructura de producción.
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="px-0">
            <Row>
                <Col xs={12} className="p-3">
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center">
                                    <h3 className="mb-0 me-3">
                                        <FontAwesomeIcon icon={faIndustry} className="me-2" />
                                        Estructura Producción
                                    </h3>
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => setShowAyudaModal(true)}
                                        title="Ayuda"
                                    >
                                        <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
                                        Ayuda
                                    </Button>
                                    <Button
                                        variant="outline-warning"
                                        size="sm"
                                        onClick={() => setShowRecuerdeModal(true)}
                                        title="Recuerde"
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                        Recuerde
                                    </Button>
                                </div>
                                <Button
                                    variant="primary"
                                    onClick={handleSave}
                                    disabled={loading}
                                >
                                    <FontAwesomeIcon icon={faSave} className="me-2" />
                                    Guardar
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                            {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <Form.Group>
                                        <Form.Label>Descripción de la Estructura de Producción</Form.Label>
                                        <ReactQuill
                                            theme="snow"
                                            value={estructuraProduccion}
                                            onChange={setEstructuraProduccion}
                                            style={{ height: '300px', marginBottom: '50px' }}
                                            modules={{
                                                toolbar: [
                                                    [{ 'header': [1, 2, 3, false] }],
                                                    ['bold', 'italic', 'underline', 'strike'],
                                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                    [{ 'align': [] }],
                                                    ['link'],
                                                    ['clean']
                                                ]
                                            }}
                                        />
                                    </Form.Group>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modal de Ayuda */}
            <Modal show={showAyudaModal} onHide={() => setShowAyudaModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Ayuda</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ textAlign: 'justify' }}>
                        Plantee si se vienen produciendo cambios tecnológicos o de alguna índole en la actividad económica donde se participaría y si ello favorece o no la propuesta de inversión planteada.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAyudaModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Recuerde */}
            <Modal show={showRecuerdeModal} onHide={() => setShowRecuerdeModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Recuerde</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ textAlign: 'justify' }}>
                        Si gracias a innovaciones e inventos y nuevos procesos tecnológicos aprovechables, es viable incursionar en un negocio, recortando y superando la curva de experiencia de los competidores antiguos, se está frente a una oportunidad importante.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRecuerdeModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Ranked - Calificación */}
            {(() => {
                const calificacion = <Ranked field={rankedForm} ciemId={ciemId} />;
                return <CardLayout value={"rankedFourteen"} tablaComponent={calificacion} />;
            })()}
        </Container>
    );
};

export default EstructuraProduccionPage;
